import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content
 * or continuously scrolling as a marquee banner.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [slidesContainer]
 * @property {HTMLButtonElement} [closeButton]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('scrolling')) {
      this.initScrolling();
      return;
    }

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.hasAttribute('scrolling')) {
      window.removeEventListener('resize', this.#handleResize);
      return;
    }
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /*  Marquee / Scrolling Mode Methods */

  initScrolling() {
    if (this.#isDismissed()) {
      const barWrapper = this.closest('aside') || this.parentElement;
      if (barWrapper) {
        barWrapper.style.display = 'none';
      } else {
        this.style.display = 'none';
      }
      return;
    }

    // Only wire up dismiss if the section has it enabled
    if (this.hasAttribute('dismissible')) {
      this.refs.closeButton?.addEventListener('click', () => this.dismiss());
    }

    this.setupMarqueeTrack();

    window.addEventListener('resize', this.#handleResize);
  }

  setupMarqueeTrack() {
    const container = this.refs.slidesContainer || this.querySelector('.announcement-bar__slides');
    if (!container) return;

    let group = container.querySelector('.scrolling-announcement-bar__group');
    let track = container.querySelector('.scrolling-announcement-bar__track');

    if (!track || !group) {
      const slides = Array.from(container.querySelectorAll('slideshow-slide'));
      if (slides.length === 0) {
        requestAnimationFrame(() => this.setupMarqueeTrack());
        return;
      }

      // Read the divider character from the CSS custom property (set by Liquid inline style)
      const dividerChar = getComputedStyle(this).getPropertyValue('--announcement-bar-divider').trim().replace(/^['"]|['"]$/g, '') || '';

      track = document.createElement('div');
      track.className = 'scrolling-announcement-bar__track';

      group = document.createElement('div');
      group.className = 'scrolling-announcement-bar__group';

      slides.forEach((slide, index) => {
        // In marquee mode every slide in the original group is visible and
        // announced, so clear the server-rendered inert state.
        slide.setAttribute('aria-hidden', 'false');
        slide.removeAttribute('inert');
        for (const focusable of slide.querySelectorAll('a, button, [tabindex]')) {
          focusable.removeAttribute('tabindex');
        }
        group.appendChild(slide);
        // Insert divider after every slide except the last
        if (dividerChar && index < slides.length - 1) {
          const sep = document.createElement('span');
          sep.className = 'scrolling-announcement-bar__divider';
          sep.setAttribute('aria-hidden', 'true');
          sep.textContent = dividerChar;
          group.appendChild(sep);
        }
      });

      // Add a trailing divider after the last slide so the loop looks seamless
      if (dividerChar) {
        const trailingSep = document.createElement('span');
        trailingSep.className = 'scrolling-announcement-bar__divider';
        trailingSep.setAttribute('aria-hidden', 'true');
        trailingSep.textContent = dividerChar;
        group.appendChild(trailingSep);
      }

      track.appendChild(group);
      container.appendChild(track);
    }

    const groups = Array.from(track.querySelectorAll('.scrolling-announcement-bar__group'));
    groups.forEach((g, index) => {
      if (index > 0) g.remove();
    });

    const containerWidth = this.offsetWidth || window.innerWidth;
    const groupWidth = group.offsetWidth;

    if (groupWidth === 0) {
      requestAnimationFrame(() => this.setupMarqueeTrack());
      return;
    }

    const copiesNeeded = Math.ceil(containerWidth / groupWidth) + 1;

    for (let i = 0; i < copiesNeeded - 1; i++) {
      track.appendChild(this.#createDecorativeClone(group));
    }

    const firstHalfGroups = Array.from(track.children);
    firstHalfGroups.forEach((g) => {
      track.appendChild(this.#createDecorativeClone(g));
    });
  }

  /**
   * Clones a marquee group for the seamless loop. Clones are purely decorative:
   * hide them from the accessibility tree (aria-hidden + inert) and drop their
   * links/buttons from the keyboard focus order (tabindex fallback for browsers
   * without inert support), so only the original group is announced and tabbable.
   * @param {Element} group
   * @returns {Element}
   */
  #createDecorativeClone(group) {
    const clone = group.cloneNode(true);
    if (clone instanceof Element) {
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      for (const focusable of clone.querySelectorAll('a, button, [tabindex]')) {
        focusable.setAttribute('tabindex', '-1');
      }
    }
    return clone;
  }

  #handleResize = () => {
    if (this._resizeTimeout) {
      cancelAnimationFrame(this._resizeTimeout);
    }
    this._resizeTimeout = requestAnimationFrame(() => this.setupMarqueeTrack());
  }

  dismiss() {
    // Hide the full announcement bar wrapper
    const barWrapper = this.closest('aside') || this.parentElement;
    if (barWrapper) {
      barWrapper.style.display = 'none';
    } else {
      this.style.display = 'none';
    }
    // Persist dismissed state for this session only.
    sessionStorage.setItem('announcement-bar-dismissed', '1');
  }

  #isDismissed() {
    return sessionStorage.getItem('announcement-bar-dismissed') === '1';
  }

  /* Slideshow / Fade Mode Methods */

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      const hidden = index !== relativeIndex;
      slide.setAttribute('aria-hidden', `${hidden}`);
      // Keep the tab order in step with the accessibility tree: an aria-hidden
      // slide whose link stays focusable is an invisible focus stop (ECR-304).
      slide.toggleAttribute('inert', hidden);
      for (const focusable of slide.querySelectorAll('a, button, [tabindex]')) {
        if (hidden) focusable.setAttribute('tabindex', '-1');
        else focusable.removeAttribute('tabindex');
      }
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
